<script setup lang="ts">
import { Smartphone } from 'lucide-vue-next'

import { PREVIEW_ZONE_ATTRIBUTE, PREVIEW_ZONE_KEY } from '../composables/previewZone'
import type { AppearanceTab } from '../composables/webappPageTabs'

const props = withDefaults(defineProps<{ zone: AppearanceTab; padded?: boolean }>(), { padded: true })

const { t } = useI18n()
const state = inject(PREVIEW_ZONE_KEY, null)
const id = useId()

const isActive = computed(() => state?.activeBlock.value === id)
const isShown = computed(
  () => isActive.value || (state?.activeBlock.value === null && state?.zone.value === props.zone)
)
const label = computed(() =>
  t(`pages.loyaltySupports.webapp.previewZones.${isActive.value && state ? state.zone.value : props.zone}`)
)

const zoneOf = (target: EventTarget | null): AppearanceTab => {
  const element = target instanceof Element ? target.closest<HTMLElement>(`[${PREVIEW_ZONE_ATTRIBUTE}]`) : null

  return (element?.getAttribute(PREVIEW_ZONE_ATTRIBUTE) as AppearanceTab | null) ?? props.zone
}

const activate = (event: Event) => {
  if (!state) return

  state.zone.value = zoneOf(event.target)
  state.activeBlock.value = id
}
</script>

<template>
  <section
    class="relative rounded-xl border bg-white transition-[border-color,box-shadow]"
    :class="[
      padded ? 'p-5' : '',
      isActive
        ? 'border-foreground shadow-[0_0_0_1px_var(--foreground)]'
        : 'border-border hover:border-muted-foreground'
    ]"
    @click="activate"
    @focusin="activate"
  >
    <span
      class="absolute right-5 top-5 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold"
      :class="isShown ? 'border-foreground bg-muted text-foreground' : 'border-border text-muted-foreground'"
    >
      <Smartphone class="size-3" />
      {{ label }}
    </span>
    <slot />
  </section>
</template>
